import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Link, useNavigate } from "react-router-dom";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { Briefcase, Timer, ClockClockwise, TrendUp, Warning, WarningCircle, CaretRight, Gavel, ArrowClockwise, Crown, CalendarPlus, Storefront, ArrowsClockwise, ArrowRight, Bank } from "@phosphor-icons/react";
import { format } from "date-fns";

const cycleLabel = { weekly: "Weekly", "bi-weekly": "Bi-weekly", "semi-monthly": "Semi-monthly", monthly: "Monthly" };

function TaxStrip() {
  const { data = [] } = useQuery({ queryKey: ["tax-returns"], queryFn: async () => (await api.get("/tax/returns")).data, retry: false, staleTime: 30000 });
  const latest = data[0];
  const label = latest ? `${latest.tax_year} return · ${latest.status.replace(/_/g, " ")}` : "Prepare your Canadian T1 with an approved tax preparer";
  return (
    <Link to={latest ? `/tax/${latest.id}` : "/tax"} data-testid="dashboard-tax-centre"
      className="mb-6 flex items-center gap-4 rounded-xl border border-red-200 bg-white px-5 py-4 text-slate-900 hover:-translate-y-0.5 transition-transform duration-200">
      <div className="h-10 w-10 rounded-lg bg-red-600 text-white flex items-center justify-center"><Bank className="h-5 w-5" weight="bold" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-bold">Tax Centre</p>
        <p className="text-sm text-slate-600 truncate capitalize">{label}</p>
      </div>
      {latest?.open_requests > 0 && <span className="text-xs font-semibold text-red-600">Action required</span>}
      <ArrowRight className="h-5 w-5 text-red-600" />
    </Link>
  );
}

function GoalsStrip({ currency }) {
  const { data } = useQuery({
    queryKey: ["goals"],
    queryFn: async () => (await api.get("/goals")).data,
    retry: false, staleTime: 30000,
  });
  const goals = (data?.goals || []).filter((g) => g.status !== "reached").slice(0, 2);
  if (!goals.length) return null;
  return (
    <div className="mb-6 grid sm:grid-cols-2 gap-4" data-testid="dashboard-goals">
      {goals.map((g) => {
        const plan = g.recommended;
        const hint = plan
          ? (plan.parts ? `~${plan.parts.map((p) => `${p.units} ${p.unit_label}`).join(" + ")}`
            : plan.detail ? `~${plan.detail}` : null)
          : null;
        return (
          <Link key={g.id} to="/goals" className="block group" data-testid={`dash-goal-${g.id}`}>
            <Card className="p-4 bg-card border-border h-full transition-colors duration-200 group-hover:border-primary/50">
              <div className="flex items-center justify-between mb-2">
                <p className="font-bold flex items-center gap-2"><span className="text-lg">{g.icon}</span>{g.name}</p>
                <span className="text-xs text-muted-foreground">{g.days_remaining >= 0 ? `${g.days_remaining}d left` : "overdue"}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-semibold">{money(g.saved, currency)} <span className="text-muted-foreground font-normal">/ {money(g.target_amount, currency)}</span></span>
                <span className="text-muted-foreground">{g.progress_pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-primary to-emerald-400" style={{ width: `${Math.min(100, g.progress_pct)}%` }} />
              </div>
              {hint && <p className="text-xs text-muted-foreground mt-2">{hint} to reach it →</p>}
            </Card>
          </Link>
        );
      })}
    </div>
  );
}


function TrendingStrip() {
  const { data } = useQuery({
    queryKey: ["mkt-dashboard-summary"],
    queryFn: async () => (await api.get("/marketplace/dashboard-summary")).data,
    retry: false, staleTime: 30000,
  });
  if (!data?.has_profile) return null;
  const hasActivity = data.open_shifts + data.open_swaps > 0;
  return (
    <Link to="/marketplace" data-testid="trending-strip" className="block mb-6 group">
      <Card className="p-4 bg-gradient-to-r from-primary/10 to-transparent border-primary/25 transition-colors duration-200 group-hover:border-primary/50">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0"><Storefront className="h-5 w-5 text-primary" weight="bold" /></div>
            <div className="min-w-0">
              <p className="text-xs overline text-primary">Trending in {data.organization_name}</p>
              {hasActivity ? (
                <p className="text-sm font-bold" data-testid="trending-counts">
                  <span className="text-emerald-400">{data.open_shifts}</span> open shift{data.open_shifts === 1 ? "" : "s"}
                  {" · "}<span className="text-sky-400">{data.open_swaps}</span> swap{data.open_swaps === 1 ? "" : "s"} available now
                  {data.matched_count > 0 && <span className="text-primary" data-testid="trending-matched"> · ⭐ {data.matched_count} match your alerts</span>}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground" data-testid="trending-empty">No open shifts right now — be the first to post one.</p>
              )}
              {data.potential_earnings > 0 && <p className="text-xs text-emerald-400 font-semibold mt-0.5" data-testid="trending-potential">➕ Potential additional earnings: +${data.potential_earnings}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {(data.teasers || []).slice(0, 3).map((t) => (
              <span key={t.id} className={`hidden md:inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full ${t.matched ? "bg-primary/20 text-primary font-semibold" : "bg-secondary/60 text-muted-foreground"}`} data-testid={`trending-teaser-${t.id}`} title={t.matched ? `Matches: ${t.alert_name}` : undefined}>
                {t.matched ? "⭐ " : (t.listing_type === "swap" ? <ArrowsClockwise className="h-3 w-3" weight="bold" /> : <Storefront className="h-3 w-3" weight="bold" />)}
                {t.shift_type}{t.unit ? ` · ${t.unit}` : ""}
              </span>
            ))}
            <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">View <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" /></span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function StatCard({ icon: Icon, label, amount, hours, currency, accent, testId, to }) {
  return (
    <Link to={to} data-testid={testId} className="block">
      <Card className="p-5 bg-card border-border hover:-translate-y-1 transition-transform duration-200 h-full">
        <div className="flex items-center justify-between mb-4">
          <span className="overline text-muted-foreground">{label}</span>
          <div className={`h-9 w-9 rounded-md flex items-center justify-center ${accent}`}>
            <Icon className="h-5 w-5" weight="bold" />
          </div>
        </div>
        <p className="text-2xl font-black tracking-tight">{money(amount, currency)}</p>
        <p className="text-xs text-muted-foreground mt-1">{hours} hrs logged this period</p>
      </Card>
    </Link>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["dashboard"],
    queryFn: async () => (await api.get("/dashboard")).data,
    retry: false,
  });

  const { data: limits } = useQuery({
    queryKey: ["billing-limits"],
    queryFn: async () => (await api.get("/billing/limits")).data,
    retry: false,
  });

  if (isError) {
    if (error?.response?.status === 401) {
      navigate("/login");
      return null;
    }
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center animate-fade-up" data-testid="dashboard-error">
        <WarningCircle className="h-10 w-10 text-destructive mb-3" weight="fill" />
        <p className="text-lg font-semibold">Couldn't load your dashboard</p>
        <p className="text-muted-foreground text-sm mt-1 mb-5">Something went wrong while fetching your pay data.</p>
        <Button onClick={() => refetch()} data-testid="dashboard-retry" className="rounded-md font-semibold">
          <ArrowClockwise className="h-4 w-4 mr-1.5" weight="bold" /> Retry
        </Button>
      </div>
    );
  }

  if (isLoading || !data) {
    return <div className="text-muted-foreground animate-pulse" data-testid="dashboard-loading">Loading dashboard…</div>;
  }

  const currency = data.currency;
  const noRate = !data.base_hourly_rate;

  const showUsageNudge =
    limits && limits.subscription_plan === "Free" &&
    limits.paid_duty_limit != null && limits.paid_duty_used >= 15;

  return (
    <div className="animate-fade-up" data-testid="dashboard-page">
      <AnnouncementBanner />

      {showUsageNudge && (
        <Card className="p-4 mb-6 bg-primary/10 border-primary/30 flex items-center justify-between gap-4" data-testid="free-usage-banner">
          <div className="flex items-start gap-3 min-w-0">
            <Crown className="h-6 w-6 text-primary shrink-0" weight="fill" />
            <div className="min-w-0">
              <p className="font-bold text-sm">
                You're using {limits.paid_duty_used}/{limits.paid_duty_limit} free entries.
              </p>
              <p className="text-sm text-muted-foreground">Upgrade to Pro for unlimited Paid Duty tracking.</p>
            </div>
          </div>
          <Button onClick={() => navigate("/settings/billing")} data-testid="free-usage-banner-cta"
            className="rounded-md font-semibold shrink-0">
            <Crown className="h-4 w-4 mr-1.5" weight="fill" /> Upgrade
          </Button>
        </Card>
      )}

      <p className="overline text-primary">Current Pay Period</p>
      <div className="flex flex-wrap items-end justify-between gap-4 mt-1 mb-8">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">
          Hello, {user?.name?.split(" ")[0] || "Officer"}
        </h1>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">
            {cycleLabel[data.cycle]} · {format(new Date(data.period_start + "T00:00:00"), "MMM d")} –{" "}
            {format(new Date(data.period_end + "T00:00:00"), "MMM d, yyyy")}
          </p>
        </div>
      </div>

      <TrendingStrip />

      {noRate && (
        <Card className="p-4 mb-6 bg-warning/10 border-warning/30 flex items-center gap-3" data-testid="no-rate-warning">
          <Warning className="h-5 w-5 text-warning shrink-0" weight="fill" />
          <span className="text-sm">
            Set your base hourly rate in{" "}
            <Link to="/settings" className="text-primary font-semibold hover:underline">Settings</Link>{" "}
            to calculate regular &amp; overtime pay.
          </span>
        </Card>
      )}

      {data.underpayment_alert && (
        <Link to={`/paycheques/${data.underpayment_alert.paycheque_id}`} data-testid="underpayment-alert">
          <Card className="p-5 mb-6 bg-destructive/15 border-destructive/50 hover:-translate-y-0.5 transition-transform duration-200">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-start gap-3 min-w-0">
                <WarningCircle className="h-7 w-7 text-destructive shrink-0" weight="fill" />
                <div className="min-w-0">
                  <p className="font-black text-destructive tracking-tight text-lg">Possible Underpayment Detected</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    You appear to be short{" "}
                    <span className="text-destructive font-bold">{money(data.underpayment_alert.shortfall, currency)}</span>{" "}
                    for {format(new Date(data.underpayment_alert.period_start + "T00:00:00"), "MMM d")} –{" "}
                    {format(new Date(data.underpayment_alert.period_end + "T00:00:00"), "MMM d, yyyy")}. Tap to review.
                  </p>
                </div>
              </div>
              <CaretRight className="h-5 w-5 text-destructive shrink-0" weight="bold" />
            </div>
          </Card>
        </Link>
      )}

      {/* North star metric — Estimated Net Pay */}
      <button onClick={() => setBreakdownOpen(true)} data-testid="net-pay-card" className="block w-full text-left">
        <Card className="p-8 mb-6 bg-primary/10 border-primary/30 relative overflow-hidden hover:-translate-y-0.5 transition-transform duration-200" data-testid="expected-pay-card">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-emerald-500/10 blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <TrendUp className="h-5 w-5 text-emerald-400" weight="bold" />
              <span className="overline text-emerald-400">Estimated Net Pay · This Period</span>
            </div>
            <p className="text-5xl sm:text-6xl font-black tracking-tight text-emerald-400" data-testid="estimated-net-pay">
              {money(data.net_pay, currency)}
            </p>
            <div className="mt-4 space-y-1 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="w-32">Gross Pay:</span>
                <span className="font-semibold text-foreground" data-testid="dash-gross-pay">{money(data.gross_pay, currency)}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="w-32">Total Deductions:</span>
                <span className="font-semibold text-rose-400" data-testid="dash-total-deductions">−{money(data.total_deductions, currency)}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-32 text-muted-foreground">Estimated Net Pay:</span>
                <span className="font-bold text-emerald-400">{money(data.net_pay, currency)}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">Tap for a full breakdown →</p>
          </div>
        </Card>
      </button>

      {(data.projected_pay > 0 || data.upcoming_pay > 0) && (
        <Card className="p-4 mb-6 bg-card border-border flex flex-wrap items-center justify-between gap-4" data-testid="projected-card">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-md bg-sky-500/15 flex items-center justify-center">
              <CalendarPlus className="h-5 w-5 text-sky-400" weight="bold" />
            </div>
            <div>
              <p className="text-sm font-semibold">
                Projected this period: <span className="text-sky-400 font-black" data-testid="projected-pay">{money(data.projected_pay, currency)}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                {data.projected_count} scheduled {data.projected_count === 1 ? "entry" : "entries"} · included in your estimate above
                {data.upcoming_pay > data.projected_pay && <> · <span data-testid="upcoming-pay">{money(data.upcoming_pay, currency)}</span> upcoming total</>}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate("/calendar")} data-testid="projected-view-calendar" className="rounded-md">
            View calendar
          </Button>
        </Card>
      )}

      <GoalsStrip currency={currency} />
      <TaxStrip />

      <Dialog open={breakdownOpen} onOpenChange={setBreakdownOpen}>
        <DialogContent className="bg-popover border-border max-w-md" data-testid="breakdown-dialog">
          <DialogHeader>
            <DialogTitle>Pay Breakdown</DialogTitle>            <DialogDescription className="text-xs text-muted-foreground">
              {format(new Date(data.period_start + "T00:00:00"), "MMM d")} – {format(new Date(data.period_end + "T00:00:00"), "MMM d, yyyy")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="overline text-emerald-400 mb-2">Earnings</p>
              {[
                ["Base Pay", data.base_pay], ["Regular", data.regular_pay], ["Overtime", data.overtime_pay],
                ["Paid Duty", data.paid_duty_pay], ["Court", data.court_pay],
                ["Other Earnings", data.other_pay],
                ...Object.entries(data.custom_by_type || {}),
              ].filter(([, v]) => v).map(([label, val]) => (
                <div key={label} className="flex justify-between text-sm py-1">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-semibold">{money(val, currency)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm py-2 border-t border-border mt-1 font-bold">
                <span>Gross Pay</span><span>{money(data.gross_pay, currency)}</span>
              </div>
            </div>
            <div>
              <p className="overline text-rose-400 mb-2">Deductions</p>
              {(data.deduction_lines || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No active deductions. <Link to="/deductions" className="text-primary hover:underline">Add some →</Link></p>
              ) : (
                <>
                  {data.deduction_lines.map((d) => (
                    <div key={d.id} className="flex justify-between text-sm py-1">
                      <span className="text-muted-foreground">{d.name}{d.kind === "percent" ? ` (${d.value}%)` : ""}</span>
                      <span className="font-semibold text-rose-400">−{money(d.amount, currency)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm py-2 border-t border-border mt-1 font-bold text-rose-400">
                    <span>Total Deductions</span><span>−{money(data.total_deductions, currency)}</span>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-between items-center py-3 px-4 bg-emerald-500/10 rounded-md border border-emerald-500/30">
              <span className="font-bold">Estimated Net Pay</span>
              <span className="text-2xl font-black text-emerald-400">{money(data.net_pay, currency)}</span>
            </div>
            <Link to="/deductions" onClick={() => setBreakdownOpen(false)}
              className="block text-center text-sm text-primary hover:underline" data-testid="manage-deductions-link">
              Manage deductions &amp; earnings
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={ClockClockwise} label="Regular" amount={data.regular_pay} hours={data.regular_hours}
          currency={currency} accent="bg-blue-500/20 text-blue-400" to="/regular-hours" testId="stat-regular"
        />
        <StatCard
          icon={Timer} label="Overtime" amount={data.overtime_pay} hours={data.overtime_hours}
          currency={currency} accent="bg-amber-500/20 text-amber-400" to="/overtime" testId="stat-overtime"
        />
        <StatCard
          icon={Briefcase} label="Paid Duty" amount={data.paid_duty_pay} hours={data.paid_duty_hours}
          currency={currency} accent="bg-emerald-500/20 text-emerald-400" to="/paid-duty" testId="stat-paid-duty"
        />
        <StatCard
          icon={Gavel} label="Court Time" amount={data.court_pay} hours={data.court_hours}
          currency={currency} accent="bg-indigo-500/20 text-indigo-400" to="/court" testId="stat-court"
        />
      </div>

      <div className="mt-6 text-sm text-muted-foreground">
        Base hourly rate: <span className="text-foreground font-semibold">{money(data.base_hourly_rate, currency)}/hr</span>
      </div>
    </div>
  );
}
